import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { fmtDate } from "@/lib/constants";
import { Wrench, Trash2, Plus, X, Pencil } from "lucide-react";
import { BulkBar, RowCheckbox, bulkDelete } from "@/components/BulkBar";

export default function Equipment() {
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [err, setErr] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    try {
      const [e, c] = await Promise.all([api.get("/equipment"), api.get("/customers")]);
      setCustomers(c.data);
      const cmap = Object.fromEntries(c.data.map((x) => [x.id, x.company_name]));
      setList(e.data.map((x) => ({ ...x, customer_name: cmap[x.customer_id] })));
    } catch (er) { setErr(formatApiError(er)); }
  };
  useEffect(() => { load(); }, []);

  const ids = list.map((e) => e.id);
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  const someSelected = ids.some((id) => selected.has(id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) ids.forEach((id) => n.delete(id));
    else ids.forEach((id) => n.add(id));
    return n;
  });
  const clearSel = () => setSelected(new Set());
  const doBulkDelete = async () => {
    const idsArr = Array.from(selected);
    if (!window.confirm(`Delete ${idsArr.length} equipment record(s)?`)) return;
    setBulkBusy(true);
    const ok = await bulkDelete(idsArr, (id) => api.delete(`/equipment/${id}`));
    setBulkBusy(false); clearSel(); await load();
    if (ok < idsArr.length) setErr(`${idsArr.length - ok} deletion(s) failed.`);
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-eyebrow text-primary">Inventory</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Equipment</h1>
          <p className="mt-1 text-sm text-muted-foreground">Every machine you service, indexed by customer.</p>
        </div>
        <button
          data-testid="equipment-create-button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-3.5 w-3.5" /> Add equipment
        </button>
      </header>

      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      <div className="sharp-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-border/60 bg-secondary/30 text-left">
            <tr>
              <th className="w-10 px-4 py-2">
                <RowCheckbox testid="equipment-select-all" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} />
              </th>
              <th className="px-4 py-2 label-eyebrow">Name</th>
              <th className="px-4 py-2 label-eyebrow">Customer</th>
              <th className="px-4 py-2 label-eyebrow">Brand / Model</th>
              <th className="px-4 py-2 label-eyebrow">Serial</th>
              <th className="px-4 py-2 label-eyebrow">Warranty</th>
              <th className="px-4 py-2 label-eyebrow">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((e) => (
              <tr key={e.id} data-testid={`equipment-row-${e.id}`} className="border-b border-border/40 hover:bg-secondary/40">
                <td className="px-4 py-2">
                  <RowCheckbox testid={`equipment-select-${e.id}`} checked={selected.has(e.id)} onChange={() => toggleOne(e.id)} />
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{e.name}</span>
                  </div>
                </td>
                <td className="px-4 py-2 text-muted-foreground">{e.customer_name || "—"}</td>
                <td className="px-4 py-2">{[e.brand, e.model].filter(Boolean).join(" · ") || "—"}</td>
                <td className="px-4 py-2 font-mono text-xs">{e.serial_number || "—"}</td>
                <td className="px-4 py-2 text-muted-foreground">{fmtDate(e.warranty_expiry)}</td>
                <td className="px-4 py-2">
                  <button
                    data-testid={`equipment-delete-${e.id}`}
                    onClick={async () => {
                      if (!window.confirm(`Delete equipment "${e.name}"?`)) return;
                      try { await api.delete(`/equipment/${e.id}`); setList((prev) => prev.filter((x) => x.id !== e.id)); }
                      catch (er) { setErr(formatApiError(er)); }
                    }}
                    className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:border-destructive/60 hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-xs text-muted-foreground">No equipment yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <BulkBar count={selected.size} entityLabel="equipment" onClear={clearSel} onDelete={doBulkDelete} busy={bulkBusy} />

      {creating && (
        <EquipmentCreateModal
          customers={customers}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}
      {editing && (
        <EquipmentCreateModal
          existing={editing}
          customers={customers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EquipmentCreateModal({ customers, onClose, onSaved, existing }) {
  const [form, setForm] = useState({
    name: existing?.name || "", brand: existing?.brand || "",
    model: existing?.model || "", notes: existing?.notes || "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (!payload.name) { setErr("Name is required"); setBusy(false); return; }
      if (existing) await api.patch(`/equipment/${existing.id}`, payload);
      else await api.post("/equipment", payload);
      onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl sharp-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <div>
            <p className="label-eyebrow text-primary">Register equipment</p>
            <h3 className="mt-1 font-heading text-lg font-semibold">{existing ? `Edit — ${existing.name}` : "Add new equipment"}</h3>
          </div>
          <button data-testid="equipment-modal-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:bg-secondary">
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Equipment name *</label>
            <input data-testid="equipment-form-name" required value={form.name} onChange={set("name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="e.g. HP LaserJet Pro" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Brand</label>
            <input data-testid="equipment-form-brand" value={form.brand} onChange={set("brand")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Model No</label>
            <input data-testid="equipment-form-model" value={form.model} onChange={set("model")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="e.g. M404dn" />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Notes</label>
            <textarea data-testid="equipment-form-notes" value={form.notes} onChange={set("notes")} rows={2} className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          {err && <div className="sm:col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="equipment-form-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : (existing ? "Save changes" : "Add equipment")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
